import React, {useContext, useState} from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import {useWorkspaceContext} from '../../../context/workspaceContext';
import {CanvasActionType} from '../../../@types/canvas-action-type';
import * as canvasUtils from '../../../utils/canvas/node-utils'
import Button from '@mui/material/Button';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {FaultTreeNode} from '../../../@types/faultTreeNode';
import {hasRightToEdit} from '../../../utils/role-to-rights';
import {UserContext} from '../../../context/userContext';

export default function FaultTreeNodeMenu(): React.ReactElement {
    const { currentWorkspaceObject, currentCanvasSelectedNode, setCurrentCanvasAction } = useWorkspaceContext();
    const { user } = useContext(UserContext);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = (): void => {
        setAnchorEl(null);
    };

    const canPerform = (perform: (node: FaultTreeNode) => boolean): boolean => {
        return hasRightToEdit(currentWorkspaceObject!, user?.superUser) && currentCanvasSelectedNode !== null && perform(currentCanvasSelectedNode as FaultTreeNode);
    }

    const setCanvasAction = (type: CanvasActionType): void => {
        setCurrentCanvasAction({ actionType: type });
        handleClose();
    }

    const notBaseEvent = (node: FaultTreeNode): boolean => {
        return node.type !== 'BASE_EVENT';
    }

    const canReorderChildren = (node: FaultTreeNode): boolean => {
        let childIds = node.childIds;
        return node.type !== 'BASE_EVENT' && childIds?.length > 1;
    }

    return (
        <>
            <Button
                id="fault-tree-node-menu-button"
                aria-controls={open ? 'fault-tree-node-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={anchorEl ? 'true' : undefined}
                aria-label="Fault Tree Node Menu"
                onClick={handleClick}
                endIcon={<ArrowDropDownIcon />}
                style={{ color: 'white', textTransform: 'none', }}
                sx={{
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                }}
            >
                Fault Tree Node
            </Button>
            <Menu
                id="fault-tree-node-menu"
                aria-labelledby="fault-tree-node-menu-button"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slotProps={{
                    list: {
                        'aria-labelledby': 'fault-tree-node-menu-button',
                    },
                }}
            >
                <MenuItem onClick={(): void => { setCanvasAction(CanvasActionType.FAULTTREENODE_ADD_NODE) }} disabled={!(canPerform(notBaseEvent))} dense>Add Fault Tree Node</MenuItem>
                <MenuItem onClick={(): void => { setCanvasAction(CanvasActionType.FAULTTREENODE_COPY_SUBTREE) }} disabled={!hasRightToEdit(currentWorkspaceObject!, user?.superUser)} dense>Copy Subtree</MenuItem>
                <MenuItem onClick={(): void => { setCanvasAction(CanvasActionType.FAULTTREENODE_PASTE_SUBTREE) }} disabled={!(canPerform(canvasUtils.canPasteFaultTreeNode))} dense>Paste Subtree</MenuItem>
                <MenuItem onClick={(): void => { setCanvasAction(CanvasActionType.FAULTTREENODE_DELETE_NODE) }} disabled={!hasRightToEdit(currentWorkspaceObject!, user?.superUser)} dense>Delete Fault Tree Node</MenuItem>
                <Divider />
                <MenuItem onClick={(): void => { setCanvasAction(CanvasActionType.FAULTTREENODE_REORDER_CHILDREN) }} disabled={!(canPerform(canReorderChildren))} dense>Reorder Children</MenuItem>
                <Divider />
                <MenuItem onClick={(): void => { setCanvasAction(CanvasActionType.NODE_PROPERTIES) }} disabled={!hasRightToEdit(currentWorkspaceObject!, user?.superUser)} dense>Fault Tree Node Properties</MenuItem>
            </Menu>
        </>
    );
}
